'use client';
import Input from '@ui/Input';
import React, { useEffect } from 'react';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { defaults, pick } from 'lodash-es';
import { authorizedServerFetch } from 'actions/server.actions';
import { getEnvBaseUrl } from '@/utils/stringHelpers';
import StyledContainedButton from '@/components/Styled/StyledContainedButton';

function EditProfileStep(props) {
  const { userProfile, onSubmit, onBack, formData, onNext, saving } = props;

  const envUrl = getEnvBaseUrl();

  const defaultValues = defaults(
    pick(formData, [
      'firstName',
      'lastName',
      'designation',
      'company',
      'location',
      'profileUrl',
    ]),
    {
      firstName: userProfile ? userProfile.first_name : '',
      lastName: userProfile ? userProfile.last_name : '',
      designation: userProfile ? userProfile.designation : '',
      company: userProfile ? userProfile.companyName : '',
      location: userProfile ? userProfile.locationDetails : '',
      profileUrl: userProfile?.profileUrl
        ? `${envUrl}/profile/${userProfile.profileUrl}`
        : `${envUrl}/profile/${userProfile?.first_name
            ?.trim()
            .toLowerCase()}-${userProfile?.last_name?.trim().toLowerCase()}-${
            userProfile?.id
          }`,
    },
  );

  const validateProfileUrl = async (url) => {
    try {
      // Make an API call to check if the email is already registered
      const { data } = await authorizedServerFetch(
        `common/auth/profile/verify/${url}`,
      );
      return data?.data?.exists;
    } catch (error) {
      // Handle API call error
      console.error('Error checking profile url:', error);
      return 'Failed to validate profile url';
    }
  };

  const schema = yup.object().shape({
    firstName: yup
      .string()
      .required('First name is required')
      .min(2, 'First Name is too short')
      .max(50, 'First name must be less than 50 characters long'),
    lastName: yup
      .string()
      .required('Last name is required')
      .min(2, 'Last Name is too short')
      .max(50, 'Last name must be less than 50 characters long'),
    designation: yup
      .string()
      .required('Designation is required')
      .max(50, 'Designation must be less than 50 characters long'),
    company: yup
      .string()
      .required('Company is required')
      .min(2, 'Company is too short')
      .max(50, 'Company must be less than 50 characters long'),
    location: yup
      .string()
      .required('Location is required')
      .min(2, 'Location is too short')
      .max(50, 'Location must be less than 50 characters long'),
    profileUrl: yup
      .string()
      .required('Profile Url is required')
      .test(
        'is-valid-profile-url',
        'Username/Slug should only consist of letters, numbers, hyphens, and underscores',
        (value) => {
          if (!value) return true; // Skip validation if the field is empty
          const trimmedValue = value.substring(value.lastIndexOf('/') + 1);
          // return /^[\w-]+$/.test(trimmedValue);
          return /^[a-zA-Z0-9_-]+$/.test(trimmedValue);
        },
      ),
    // .test(
    //   'unique-profile-url',
    //   'Username/Slug is already taken. Please use a different one.',
    //   async (value) => {
    //     if (!value) return true; // Skip validation if the profile url field is empty
    //     const trimmedValue = value.substring(value.lastIndexOf('/') + 1);
    //     const isProfileUrlAvailable = await validateProfileUrl(trimmedValue);
    //     return !isProfileUrlAvailable;
    //   },
    // ),
  });

  const form = useForm({
    defaultValues: defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    trigger,
    setError,
  } = form;

  const handleFormSubmit = async (data) => {
    let payload = { ...data };
    if (payload.profileUrl) {
      payload.profileUrl = payload.profileUrl.substring(
        payload.profileUrl.lastIndexOf('/') + 1,
      );
      const profileUrlExists = await validateProfileUrl(payload.profileUrl);
      if (profileUrlExists) {
        setError('profileUrl', {
          type: 'manual',
          message:
            'Username/Slug is already taken. Please use a different one.',
        });
        return;
      }
    }
    onSubmit(payload, 'Profile Information');
    // onNext();
  };

  useEffect(() => {
    trigger();
  }, [userProfile, formData]);

  return (
    <div className="flex w-full flex-1 flex-col gap-12">
      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className="flex flex-col gap-6">
        <div className="flex gap-6">
          <div className="flex w-1/2 flex-col">
            <Controller
              name="firstName"
              control={control}
              render={({ field: { ref, ...rest } }) => (
                <Input
                  label="FIRST NAME"
                  required
                  {...rest}
                  error={errors.firstName?.message}
                />
              )}
            />
          </div>
          <div className="flex w-1/2 flex-col">
            <Controller
              name="lastName"
              control={control}
              render={({ field: { ref, ...rest } }) => (
                <Input
                  label="LAST NAME"
                  required
                  {...rest}
                  error={errors.lastName?.message}
                />
              )}
            />
          </div>
        </div>

        <div className="flex gap-6">
          <div className="flex w-1/2 flex-col">
            <Controller
              name="designation"
              control={control}
              render={({ field: { ref, ...rest } }) => (
                <Input
                  label="JOB TITLE"
                  required
                  {...rest}
                  error={errors.designation?.message}
                />
              )}
            />
          </div>
          <div className="flex w-1/2 flex-col">
            <Controller
              name="company"
              control={control}
              render={({ field: { ref, ...rest } }) => (
                <Input
                  label="COMPANY"
                  required
                  {...rest}
                  error={errors.company?.message}
                />
              )}
            />
          </div>
        </div>

        <Controller
          name="location"
          control={control}
          render={({ field: { ref, ...rest } }) => (
            <Input
              label="LOCATION"
              required
              {...rest}
              error={errors.location?.message}
            />
          )}
        />

        <Controller
          name="profileUrl"
          control={control}
          render={({ field: { ref, ...rest } }) => (
            <Input
              label="PROFILE URL"
              required
              placeholder={`${envUrl}/<username-or-slug>`}
              {...rest}
              error={errors.profileUrl?.message}
              onChange={(e) => {
                const inputValue = e.target.value.trim();
                const currentUrl = rest.value || ''; // Get the current value of profileUrl
                const profileIndex =
                  currentUrl.indexOf('/profile/') + '/profile/'.length;

                if (
                  inputValue.length &&
                  inputValue.startsWith(`${envUrl}/profile/`)
                ) {
                  rest.onChange(inputValue);
                } else {
                  // Extract the part of the URL that comes after "/profile/"
                  const newUrlPart = inputValue.substring(profileIndex);

                  // Build the new URL by combining the base URL and the new part
                  const newUrl = `${envUrl}/profile/${newUrlPart}`;

                  rest.onChange(newUrl);
                }
              }}
            />
          )}
        />

        <div className="flex-rows flex justify-end gap-4 pb-4">
          {/* {isValid ? (
            <div className="flex-flow-col">
              <StyledContainedButton
                variant="filled"
                color="primary"
                type="submit"
                className="mt-2"
                loading={saving}
                // onClick={onNext}
              >
                Save
              </StyledContainedButton>
            </div>
          ) : (
            <div className="flex-flow-col">
              <StyledContainedButton
                disabled={true}
                variant="outlined"
                color="primary"
                type="submit"
                className="mt-2"
                // onClick={onNext}
              >
                Save
              </StyledContainedButton>
            </div>
          )} */}
          <StyledContainedButton
            disabled={!isValid || saving} // Disable the button if form is invalid
            variant="filled"
            color="primary"
            type="submit"
            className="mt-2"
            loading={saving}>
            Save
          </StyledContainedButton>
        </div>
      </form>
    </div>
  );
}

export default EditProfileStep;
